/* ---------------------------------------------------------------------------
   How a bridge is framed, once, for every view in this application that shows one.

   THE MODELLER'S OWN ZOOM ALL, copied deliberately. `Canvas3D.ZoomAll` in
   vendor/bridge-modeler/src/canvas/Canvas3D.ts is what an engineer who has used
   the modeller expects a bridge to look like when a view opens or a Zoom All is
   pressed: a three-quarter view from a fixed direction, at the distance that just
   fits the structure's bounding sphere. Two views in this application draw the
   same `.glb` — the record's hero panel (./meshView.ts) and the Defect Inspector
   (src/inspector/scene.ts) — and before this module they framed it two different
   ways, neither of them the modeller's.

   THREE PLACES, ONE ANSWER, AND THAT IS THE WHOLE POINT. A defect is placed in the
   Inspector, reviewed on the record and re-examined in the modeller, and a
   structure that looks like a different size and sits at a different angle in each
   of them is three pictures of one bridge. Copying two numbers into two files would
   have got today's behaviour; a module both import is what keeps them together the
   next time one is touched.

   WHAT IS NOT FRAMED THIS WAY, and why: the Site view (src/map/scene3d.ts). It
   draws the structure standing on real terrain at its own coordinates, and the
   question it answers is "where is this bridge" rather than "what does this bridge
   look like" — so its camera is derived from the terrain patch's span and
   deliberately keeps the valley in shot. Framing it on the structure's own bounding
   sphere would crop away the thing it is for.

   THREE.JS IS PASSED IN, never imported. Both callers load it as a ~600 kB dynamic
   chunk on demand, and a static import here would pull it into whatever bundle
   touches this file — which is exactly the trap src/data/componentTree.ts avoids by
   being written against a structural interface. Nothing in this module holds state;
   it is two functions over a scene somebody else owns.
   --------------------------------------------------------------------------- */

import type CameraControls from 'camera-controls';

/**
 * The direction a bridge is looked at from.
 *
 * `viewDir` in `Canvas3D.ZoomAll` reflected in the deck's axis: 45° round from the
 * model's own axes and about 23° up, from the other side. It is a fixed direction
 * rather than one derived from the structure's proportions because it has to be the
 * SAME direction on every record — and because 45° is what makes a bridge read as a
 * bridge. Square on it is an elevation drawing, which is a real view and is what the
 * standard-view buttons are for; from directly above it is a plan. Between the two you can see
 * that the deck has a soffit and that the piers have depth, which is what somebody
 * opening a 3D view came for.
 *
 * A QUARTER TURN ROUND FROM THE MODELLER, AND THAT PART IS OURS. `Canvas3D.ZoomAll`
 * stands at (1, 0.6, 1) — south-west of the structure, in the terms the register uses
 * — and this stands at (-1, 0.6, 1), south-east: the other side of the deck's axis,
 * same height, same 45°. It is the one number in this module that is not the
 * modeller's, so it is the one that needs a reason: on the bridges this register
 * holds, the modeller's own quarter puts the near abutment in the far corner of the
 * frame and the deck running back across it, and the view that reads as the bridge
 * somebody is about to inspect is the one from the opposite side. Everything else
 * about the shot is unchanged, so it is the same photograph taken from the other
 * quarter rather than a different framing.
 *
 * ONLY THE SIGN IS OURS, and the check on this holds it that way: it reads the three
 * numbers out of `Canvas3D.ZoomAll` and asserts this is them with x negated. So the
 * 45° and the 0.6 still come from the vendored source, and the day somebody retunes
 * the modeller's Zoom All it still says which of the two moved.
 *
 * FIXED IN THE WORLD, NOT IN THE BRIDGE, and this was tried the other way round and
 * was wrong. The argument for resolving it against the model's own axes is that the
 * modeller BUILDS along +X while a `.glb` from a CSiBridge export runs along whichever
 * horizontal axis the source used — the very reason the Inspector's six standard views
 * measure `along` and `across` (src/inspector/scene.ts, `axialFrame`). It does not
 * follow, and the difference is what a Zoom All is FOR. The six views are named after
 * what they show: Front must be the long elevation, so it has to find the deck. The
 * three-quarter view is not named after anything — it is one fixed place to stand, and
 * its whole job is that a bridge opens at the same angle every time, on every record.
 * Measuring the deck and standing 45° off IT re-derives a direction from the file's own
 * frame, and on a model whose deck runs along Z that lands the camera a quarter turn
 * round from where it lands on every other model: the same structure, mirrored across
 * the screen, for a difference nobody looking at the bridge can see a reason for.
 *
 * Not normalised here. Both consumers scale it by a distance, so its length never
 * reaches the camera — and writing the modeller's three numbers as the modeller
 * wrote them is worth more than pre-dividing them by 1.562.
 */
export const MODEL_VIEW_DIR = { x: -1, y: 0.6, z: 1 } as const;

/**
 * The vertical field of view every model view opens with, in degrees.
 *
 * 30°, not the modeller's 55. A sphere fit puts the structure at the same size on
 * screen at any field of view, so the framing is unchanged; what the narrower lens
 * changes is how far away the camera stands to get it. At 55° a zoom towards a pier
 * brought the camera to within a few metres of the deck, and the near girders and
 * barrier ballooned over everything behind them. At 30° the same zoom stands nearly
 * twice as far back, and the bridge keeps its proportions.
 */
export const MODEL_FOV = 30;

/**
 * The bounding box of everything VISIBLE under `root`, or null if that is nothing.
 *
 * PER VISIBLE CHILD, unioned, rather than one `setFromObject(root)` over the lot.
 * `Box3.setFromObject` walks everything regardless of `visible`, so a hidden part
 * would still push the camera back to make room for something nobody can see. The
 * modeller does it this way (`Canvas3D.ZoomAll`, and `SetStandardView` beside it)
 * because it hides components; this application does not yet, and copying the rule
 * now costs a loop and means an isolate-selection can land later without quietly
 * breaking every framing in the app.
 *
 * The box rather than the sphere, because the two consumers want different things
 * from it: a Zoom All wants the sphere below, and an axial view wants the extents
 * along each axis. One measurement, read two ways.
 */
export function visibleBounds(
  three: typeof import('three'),
  root: import('three').Object3D,
): import('three').Box3 | null {
  const box = new three.Box3();
  const one = new three.Box3();
  let found = false;
  for (const child of root.children) {
    if (!child.visible) continue;
    one.setFromObject(child);
    if (one.isEmpty()) continue;
    box.union(one);
    found = true;
  }
  return found && !box.isEmpty() ? box : null;
}

/**
 * The bounding sphere of everything visible under `root`, or null if that is
 * nothing.
 *
 * A SPHERE, NOT A BOX, and the modeller gives the reason: the camera sits along one
 * fixed diagonal, and a sphere captures every dimension the model might be turned
 * to show next — so a Zoom All followed by an orbit does not immediately need
 * another Zoom All. A box fit is tighter and is the right answer for a view locked
 * to an axis, which is why the Inspector's standard views measure their own extents
 * off `visibleBounds` instead.
 */
export function modelBounds(
  three: typeof import('three'),
  root: import('three').Object3D,
): import('three').Sphere | null {
  const box = visibleBounds(three, root);
  return box ? box.getBoundingSphere(new three.Sphere()) : null;
}

/**
 * Point the camera at the whole structure, from the direction above.
 *
 * NO PADDING, which is the modeller's own choice and worth stating because it looks
 * like an omission. `Canvas3D.ZoomAll` defaults `paddingFactor` to 1.0 and every
 * call site in App.ts takes the default, so the sphere is inscribed exactly. It
 * still leaves air around the BRIDGE: a 140 m structure 12 m wide has a bounding
 * sphere far larger than its own silhouette in every direction but one, so fitting
 * the sphere puts the deck at about three-fifths of the frame. The Inspector used to
 * multiply by 1.15 on top of this and the result was a small bridge in a large empty
 * canvas.
 *
 * THE DISTANCE IS THE LIBRARY'S, not arithmetic of ours. `getDistanceToFitSphere`
 * is `radius / sin(min(vFOV, hFOV) / 2)` — it fits the NARROWER of the two fields
 * of view, which is the horizontal one on any portrait canvas. Getting that wrong
 * is what used to push a long bridge's ends off both sides of a phone, and it is
 * now one line in a library that both the modeller and this call.
 *
 * ONE `setLookAt` WHERE THE MODELLER USES TWO CALLS, and this is the only place
 * the two deliberately differ. `Canvas3D.ZoomAll` snaps the camera to a UNIT
 * distance along the view direction to establish the angle and then animates
 * `fitToSphere` outward, so an animated Zoom All starts with one frame taken from
 * inside the model. The camera lands in exactly the same place either way, so the
 * VIEW is copied; solving the distance first and moving once just spares the flight
 * out of the deck.
 */
export function frameModel(
  three: typeof import('three'),
  controls: CameraControls,
  sphere: import('three').Sphere,
  animate: boolean,
): void {
  const dir = new three.Vector3(MODEL_VIEW_DIR.x, MODEL_VIEW_DIR.y, MODEL_VIEW_DIR.z).normalize();
  const distance = controls.getDistanceToFitSphere(sphere.radius);
  const c = sphere.center;
  void controls.setLookAt(
    c.x + dir.x * distance,
    c.y + dir.y * distance,
    c.z + dir.z * distance,
    c.x,
    c.y,
    c.z,
    animate,
  );
}
